const OPERATION_ID_PATTERN = /^[A-Za-z0-9][A-Za-z0-9._:-]{7,127}$/;
const ALLOWED_CATEGORIES = new Set([
  "api-network-failure",
  "blocking-asset-failure",
  "client-exception",
  "initialization-failure",
  "socket-ack-timeout",
  "socket-unavailable",
]);

let installed = false;
let telemetryConfig = null;

const boundedString = (value, maximum) => (
  typeof value === "string" ? value.trim().slice(0, maximum) : null
);

const redactClientText = (value, maximum) => {
  const normalized = boundedString(value, maximum);
  if (!normalized) return null;
  return normalized
    .replace(/([?&](?:token|key|secret|password|authorization|cookie|api[_-]?key)=)[^&#\s]+/gi, "$1[redacted]")
    .replace(/\b(token|secret|password|authorization|cookie|api[_-]?key)\s*[:=]\s*[^\s,;]+/gi, "$1=[redacted]");
};

const withoutQuery = (value) => {
  const normalized = boundedString(value, 240);
  return normalized ? normalized.split(/[?#]/, 1)[0] : null;
};

const getHeader = (headers, name) => {
  if (!headers || typeof headers !== "object") return null;
  const matchingKey = Object.keys(headers).find((key) => key.toLowerCase() === name.toLowerCase());
  return matchingKey ? headers[matchingKey] : null;
};

// Axios normally retains `config` for browser failures, but browser/network
// aborts can lose it. Keep the fallback deliberately narrow and let the
// sanitiser remove any query string before this reaches server-side logs.
export const getNetworkRequestDetails = (error) => ({
  request_path: error?.config?.url
    || error?.request?.responseURL
    || error?.request?._currentUrl
    || null,
  request_method: error?.config?.method || error?.request?.method || null,
});

export const isCanceledRequestError = (error, axiosClient = null) => (
  Boolean(axiosClient?.isCancel?.(error))
  || error?.code === "ERR_CANCELED"
  || error?.name === "CanceledError"
  || error?.config?.signal?.aborted === true
);

export const normalizeOperationId = (value) => {
  if (typeof value !== "string") return null;
  const normalized = value.trim();
  return OPERATION_ID_PATTERN.test(normalized) ? normalized : null;
};

export const createOperationId = () => {
  if (window.crypto?.randomUUID) return window.crypto.randomUUID();
  if (window.crypto?.getRandomValues) {
    const values = new Uint32Array(4);
    window.crypto.getRandomValues(values);
    return `${Date.now()}-${Array.from(values).map((value) => value.toString(16)).join("")}`;
  }
  return `${Date.now()}-${Math.random().toString(36).slice(2)}-${Math.random().toString(36).slice(2)}`;
};

export const sanitizeClientTelemetry = (value = {}) => {
  const category = boundedString(value.category, 60);
  if (!ALLOWED_CATEGORIES.has(category)) return null;
  return {
    category,
    message: redactClientText(value.message, 300),
    code: boundedString(value.code, 80),
    error_name: boundedString(value.error_name, 80),
    source: withoutQuery(value.source),
    request_path: withoutQuery(value.request_path),
    request_method: boundedString(value.request_method, 12)?.toUpperCase() || null,
    online: typeof value.online === "boolean" ? value.online : null,
    elapsed_ms: Number.isFinite(Number(value.elapsed_ms))
      ? Math.max(0, Math.min(120000, Math.round(Number(value.elapsed_ms))))
      : null,
  };
};

export const shouldSampleClientTelemetry = (operationId, sampleRate) => {
  const normalizedRate = Math.max(0, Math.min(1, Number(sampleRate) || 0));
  if (normalizedRate === 0) return false;
  if (normalizedRate === 1) return true;
  let hash = 2166136261;
  for (const char of String(operationId || "")) {
    hash ^= char.charCodeAt(0);
    hash = Math.imul(hash, 16777619);
  }
  return (hash >>> 0) / 0x100000000 < normalizedRate;
};

export const reportClientTelemetry = async (payload, operationId = null) => {
  if (!telemetryConfig?.enabled || typeof window.fetch !== "function") return;
  const sanitized = sanitizeClientTelemetry(payload);
  if (!sanitized) return;
  const correlationId = normalizeOperationId(operationId) || createOperationId();
  if (!shouldSampleClientTelemetry(correlationId, telemetryConfig.sampleRate)) return;

  const controller = typeof AbortController === "function" ? new AbortController() : null;
  const timeout = window.setTimeout(() => controller?.abort(), telemetryConfig.timeoutMs);
  try {
    await window.fetch(`${telemetryConfig.apiBaseUrl.replace(/\/$/, "")}/api/client-telemetry`, {
      method: "POST",
      credentials: "include",
      keepalive: true,
      signal: controller?.signal,
      headers: {
        "Content-Type": "application/json",
        "x-api-key": telemetryConfig.apiKey,
        "x-operation-id": correlationId,
      },
      body: JSON.stringify(sanitized),
    });
  } catch (_) {
    // Telemetry is best-effort and must never replace the UI flow.
  } finally {
    window.clearTimeout(timeout);
  }
};

const installGlobalBrowserTelemetry = () => {
  window.addEventListener("error", (event) => {
    const target = event.target;
    if (target && target !== window && ["SCRIPT", "LINK"].includes(target.tagName)) {
      void reportClientTelemetry({
        category: "blocking-asset-failure",
        message: "Required script or stylesheet failed to load",
        source: target.src || target.href || null,
        online: navigator.onLine,
      });
      return;
    }
    if (event.error || event.message) {
      void reportClientTelemetry({
        category: "client-exception",
        message: event.message || "Unhandled browser exception",
        error_name: event.error?.name || null,
        source: event.filename || window.location.pathname,
        online: navigator.onLine,
      });
    }
  }, true);

  window.addEventListener("unhandledrejection", (event) => {
    const reason = event.reason;
    if (reason?.response || reason?.__r1TelemetryReported) return;
    void reportClientTelemetry({
      category: "client-exception",
      message: reason?.message || "Unhandled promise rejection",
      error_name: reason?.name || null,
      source: window.location.pathname,
      online: navigator.onLine,
    }, reason?.config?.__r1OperationId);
  });
};

export const installClientObservability = ({ axios, apiBaseUrl, apiKey }) => {
  if (installed) return;
  installed = true;
  telemetryConfig = {
    enabled: String(process.env.REACT_APP_CLIENT_TELEMETRY_ENABLED || "true").toLowerCase() !== "false",
    sampleRate: Number(process.env.REACT_APP_CLIENT_TELEMETRY_SAMPLE_RATE ?? 0.25),
    timeoutMs: Math.max(250, Math.min(5000, Number(process.env.REACT_APP_CLIENT_TELEMETRY_TIMEOUT_MS) || 1500)),
    apiBaseUrl,
    apiKey,
  };

  axios.interceptors.request.use((config) => {
    const operationId = normalizeOperationId(getHeader(config.headers, "x-operation-id")) || createOperationId();
    config.headers = { ...config.headers, "x-operation-id": operationId };
    config.__r1OperationId = operationId;
    config.__r1StartedAt = Date.now();
    return config;
  });

  axios.interceptors.response.use((response) => {
    response.operation_id = normalizeOperationId(getHeader(response.headers, "x-operation-id"))
      || response.config?.__r1OperationId
      || null;
    return response;
  }, (error) => {
    if (
      !error?.response
      && !isCanceledRequestError(error, axios)
      && !error?.config?.__r1SkipTelemetry
      && !error?.__r1TelemetryReported
    ) {
      try { error.__r1TelemetryReported = true; } catch (_) {}
      const request = getNetworkRequestDetails(error);
      void reportClientTelemetry({
        category: error.config?.r1TelemetryCategory || "api-network-failure",
        message: error.code || "API request failed before a response was received",
        code: error.code || null,
        error_name: error.name || null,
        ...request,
        online: navigator.onLine,
        elapsed_ms: error.config?.__r1StartedAt ? Date.now() - error.config.__r1StartedAt : null,
      }, error.config?.__r1OperationId);
    }
    return Promise.reject(error);
  });

  installGlobalBrowserTelemetry();
};
